/**
 * JWT Claims interface matching Azure AD token structure
 */
export interface JwtClaims {
  oid?: string; // Azure AD Object ID
  email?: string; // User email
  upn?: string; // User Principal Name
  name?: string; // Display name
  roles?: string[]; // User roles array
  exp?: number; // Expiration timestamp
  iat?: number; // Issued at timestamp
  [key: string]: unknown; // Allow other claims
}

/**
 * Parses a JWT token and extracts its claims
 * @param token - The JWT token string
 * @returns The parsed claims object
 * @throws Error if the token format is invalid
 */
export function parseJwt(token: string): JwtClaims {
  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format: expected 3 parts');
    }

    const base64Url = parts[1];
    if (!base64Url) {
      throw new Error('Invalid JWT format: missing payload');
    }

    // Convert base64url to base64
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    // Decode base64 to JSON string
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload) as JwtClaims;
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    throw new Error(
      'Invalid JWT token format. Please ensure you copied the token correctly.',
    );
  }
}

/**
 * Validates that a JWT token has the required claims
 * @param claims - The parsed JWT claims
 * @returns true if all required claims are present
 */
export function validateTokenClaims(claims: JwtClaims): boolean {
  // Required claims for our auth system
  return Boolean(claims.oid && (claims.email || claims.upn) && claims.name);
}

/**
 * Checks if a JWT token is expired
 * @param claims - The parsed JWT claims
 * @returns true if the token is expired
 */
export function isTokenExpired(claims: JwtClaims): boolean {
  if (!claims.exp) {
    // If no expiration, assume it's expired
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = claims.exp * 1000;
  return Date.now() >= expirationTime;
}
